import { ChevronLeft } from "lucide-react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import BackgroundShape from "../backgroundshape/BackgroundShape";
import { DesktopPainel } from "../headers/DesktopPainel";
import { BuscadorProps } from "../headers/headerbusca/buscador/Buscador";
import { UserPanel } from "../userpanel/UserPanel";
import { useAuth } from "shared/contexts/Auth/AuthContext";
import styles from "./PageContainer.module.scss";

interface PageContainerProps {
  title?: string;
  icon?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  actions?: ReactNode;
  pageTools?: ReactNode;
  showDesktopSearch?: boolean;
  desktopSearchProps?: BuscadorProps;
  showLogo?: boolean;
  logo?: ReactNode;
  logoPosition?: "default" | "left";
  progress?: number;
  showBack?: boolean;
  backToHome?: boolean;
}

export default function PageContainer({
  title,
  icon,
  children,
  contentClassName,
  actions,
  pageTools,
  showDesktopSearch = true,
  desktopSearchProps,
  showLogo = false,
  logo,
  logoPosition = "default",
  progress,
  showBack = true,
  backToHome = false,
}: PageContainerProps) {
  const navigate = useNavigate();
  const { perfilAtivo } = useAuth();

  const handleBack = () => {
    if (backToHome) {
      navigate("/home");
      return;
    }

    navigate(-1);
  };

  const handleLogoClick = () => {
    navigate("/home");
  };

  const logoPositionClass =
    logoPosition === "left"
      ? styles.logoPositionLeft
      : "";

  return (
    <div
      className={`${styles.page} ${
        perfilAtivo === "vendedor"
          ? styles.pageVendedor
          : styles.pageComprador
      }`}
    >
      {title && (
        <header
          className={`${styles.header} ${logoPositionClass}`}
        >
          <BackgroundShape
            size={400}
            top={-180}
            right={-80}
          />

          <div className={styles.headerTop}>
            <div className={styles.backTitle}>
              {showBack && (
                <button
                  type="button"
                  className={styles.backButton}
                  onClick={handleBack}
                  aria-label={
                    backToHome
                      ? "Voltar para o início"
                      : "Voltar"
                  }
                >
                  <ChevronLeft
                    className={styles.backIcon}
                  />

                  <span className={styles.backBtnspan}>
                    Voltar
                  </span>
                </button>
              )}

              <div className={styles.titleContainer}>
                {icon && (
                  <span className={styles.icon}>
                    {icon}
                  </span>
                )}

                <h1 className={styles.title}>
                  {title}
                </h1>
              </div>
            </div>

            <div className={styles.mobileNotifications}>
              <UserPanel modo="notificacoes" />
            </div>

            {showLogo && logo && (
              <button
                type="button"
                className={styles.logoButton}
                onClick={handleLogoClick}
                aria-label="Ir para o início"
              >
                {logo}
              </button>
            )}

            <div className={styles.pageHeaderRight}>
              <DesktopPainel
                showSearch={showDesktopSearch}
                searchProps={desktopSearchProps}
                perfilAtivo={perfilAtivo}
              />

              <UserPanel />

              {actions && (
                <div className={styles.actions}>
                  {actions}
                </div>
              )}
            </div>
          </div>

          {pageTools && (
            <div className={styles.pageTools}>
              {pageTools}
            </div>
          )}

          {typeof progress === "number" && (
            <div className={styles.progressContainer}>
              <div className={styles.progressInfo}>
                <span>Progresso</span>

                <span>
                  {Math.min(
                    Math.max(progress, 0),
                    100
                  )}
                  %
                </span>
              </div>

              <div className={styles.progressBar}>
                <div
                  style={{
                    width: `${Math.min(
                      Math.max(progress, 0),
                      100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </header>
      )}

      <main
        className={`${styles.content} ${
          contentClassName || ""
        }`}
      >
        {children}
      </main>
    </div>
  );
}